import { Box, Flex, Icon, IconButton, useBreakpointValue } from "@chakra-ui/react";
import { CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react/dist/ssr";
import { useEffect, useRef, useState } from "react";

const ProductCarousel = ({
    children,
    gap = 20,
    itemsToShow = { base: 2, md: 3, lg: 4, xl: 6 },
    itemWidth = { base: 135, sm: 173, md: 197 },
    itemHeight = { base: 145, sm: 183 },
}) => {
    const containerRef = useRef(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const totalItems = children.length;
    const currentItems = useBreakpointValue(itemsToShow) || 2;

    // Show controls according to max-width
    const shouldShowControls = useBreakpointValue({ base: true, xl: false });

    // Calculate dimensions
    const currentItemWidth = useBreakpointValue(itemWidth) || itemWidth.base;
    const totalWidth = totalItems * (currentItemWidth + gap);
    const visibleWidth = currentItems * (currentItemWidth + gap) - gap;

    useEffect(() => {
        setCurrentIndex((prev) => Math.min(prev, Math.max(0, totalItems - currentItems)));
    }, [currentItems, totalItems]);

    const handlePrev = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prev) => Math.max(0, prev - 1));
        }
    };

    const handleNext = () => {
        if (currentIndex < totalItems - currentItems) {
            setCurrentIndex((prev) => Math.min(totalItems - currentItems, prev + 1));
        }
    };

    return (
        <Box position="relative" w="100%" maxW={`${visibleWidth}px`} margin="0 auto">
            <Box
                ref={containerRef}
                overflow="hidden"
                width="100%"
                mb={shouldShowControls && totalItems > currentItems ? 4 : 0}
            >
                <Box
                    as="div"
                    animate={{
                        x: `-${currentIndex * (currentItemWidth + gap)}px`,
                        justifyContent: "flex-start",
                    }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    transitionDuration="0.3s"
                    transitionTimingFunction="ease-out"
                    transform={`translateX(-${currentIndex * (currentItemWidth + gap)}px)`}
                    style={{
                        display: "flex",
                        gap: `${gap}px`,
                        width: `${totalWidth}px`,
                    }}
                >
                    {children.length > 0 &&
                        children?.map((child, index) => (
                            <Box
                                key={index}
                                width={`${currentItemWidth}px`}
                                height={`${itemHeight}px`}
                                flexShrink={0}
                            >
                                {child}
                            </Box>
                        ))}
                </Box>
            </Box>
            {shouldShowControls && totalItems > currentItems && (
                <Flex justify="center" gap={4} mt={2}>
                    <IconButton
                        onClick={handlePrev}
                        aria-label="Anterior"
                        size="md"
                        borderRadius="full"
                        zIndex="10"
                        bg="white"
                        border="solid"
                        borderWidth="0.7px"
                        borderColor="gray.300"
                        boxShadow="md"
                        _hover={{ bg: "gray.100" }}
                        disabled={currentIndex === 0}
                        opacity={currentIndex === 0 ? 0.4 : 1}
                        cursor={currentIndex === 0 ? "not-allowed" : "pointer"}
                    >
                        <Icon boxSize={6} color="black">
                            <CaretLeftIcon />
                        </Icon>
                    </IconButton>

                    <IconButton
                        onClick={handleNext}
                        aria-label="Siguiente"
                        size="md"
                        borderRadius="full"
                        zIndex="10"
                        bg="white"
                        border="solid"
                        borderWidth="0.7px"
                        borderColor="gray.300"
                        boxShadow="md"
                        _hover={{ bg: "gray.100" }}
                        disabled={currentIndex >= totalItems - currentItems}
                        opacity={currentIndex >= totalItems - currentItems ? 0.4 : 1}
                        cursor={
                            currentIndex >= totalItems - currentItems ? "not-allowed" : "pointer"
                        }
                    >
                        <Icon boxSize={6} color="black">
                            <CaretRightIcon />
                        </Icon>
                    </IconButton>
                </Flex>
            )}
        </Box>
    );
};

export default ProductCarousel;
